import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, MapPin, Globe2, Plus, Loader2, AlertCircle } from "lucide-react";
import SiteNav from "../components/Layout/SiteNav";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { auth } from "../config/firebase";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  Eyebrow,
  EditorialShell,
  EditorialCard,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";

const buildAddressFromComponents = (components = []) => {
  const get = (type, preferShort = false) => {
    const comp = components.find((c) => (c.types || []).includes(type));
    if (!comp) return "";
    return preferShort ? comp.short_name || comp.long_name || "" : comp.long_name || comp.short_name || "";
  };
  const streetNumber = get("street_number");
  const route = get("route");
  const city =
    get("locality") ||
    get("sublocality_level_1") ||
    get("sublocality") ||
    get("neighborhood") ||
    get("administrative_area_level_3") ||
    get("administrative_area_level_2") ||
    "";
  const state = get("administrative_area_level_1", true);
  const zip = get("postal_code");
  const country = get("country", true) || "US";
  const address = [streetNumber, route].filter(Boolean).join(" ").trim();
  return { address, city, state, zip, country };
};

const inputClass =
  "h-12 rounded-xl border text-[15px] transition-colors focus-visible:ring-0 focus-visible:border-[#bf9a2f] focus-visible:shadow-[0_0_0_3px_rgba(191,154,47,0.15)]";

export default function MasjidSignup() {
  const palette = useEditorialPalette();
  const inputStyle = {
    background: palette.paper,
    borderColor: palette.hairline,
    color: palette.ink,
    fontFamily: EDITORIAL_FONT.sans,
  };
  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: palette.muted,
    fontFamily: EDITORIAL_FONT.sans,
  };
  const navigate = useNavigate();
  const location = useLocation();
  const fromMobile = location.state?.fromMobile === true;
  const API_URL = import.meta.env.VITE_API_URL;
  const MASJID_API = import.meta.env.VITE_MASJID_API;

  const masjidApi = useMemo(() => {
    const instance = axios.create({ baseURL: MASJID_API, timeout: 20000 });
    instance.interceptors.request.use(async (config) => {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken(false);
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return instance;
  }, [MASJID_API]);

  const coreApi = useMemo(() => {
    const instance = axios.create({ baseURL: API_URL, timeout: 15000 });
    instance.interceptors.request.use(async (config) => {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken(false);
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return instance;
  }, [API_URL]);

  const [searchTerm, setSearchTerm] = useState("");
  const [masjidResults, setMasjidResults] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");
  const [selectedMasjidId, setSelectedMasjidId] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState("");
  const [claimSuccess, setClaimSuccess] = useState("");

  const [placeQuery, setPlaceQuery] = useState("");
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
    lat: "",
    long: "",
    website: "",
  });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const selectedMasjid = useMemo(
    () => masjidResults.find((m) => m.id === selectedMasjidId) || null,
    [masjidResults, selectedMasjidId]
  );

  const showAddMasjidCta = useMemo(
    () => searchTerm.trim().length > 0 && masjidResults.length === 0,
    [masjidResults.length, searchTerm]
  );

  const sanitizeMasjid = (m = {}) => ({
    id: m.id || m.masjidId || "",
    name: m.name,
    address: m.address,
    city: m.city,
    state: m.state,
    zip: m.zip,
    country: m.country,
    lat: m.lat,
    long: m.long,
    website: m.website || "",
  });

  const loadMasjids = async (term = "") => {
    try {
      setLoadingList(true);
      setListError("");
      const response = await masjidApi.get("/v1/masjid/list", { params: { q: term } });
      const { masjids } = response.data || {};
      const results = Array.isArray(masjids) ? masjids.map((m) => sanitizeMasjid(m)) : [];
      setMasjidResults(results);
      setSelectedMasjidId((prev) => (prev && results.some((m) => m.id === prev) ? prev : null));
      setClaimError("");
      setClaimSuccess("");
    } catch (err) {
      console.error("list masjids error", err);
      setListError("Unable to load masajid right now.");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadMasjids("");
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      loadMasjids(searchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleSelectMasjid = (masjidId) => {
    setSelectedMasjidId(masjidId);
    setClaimError("");
    setClaimSuccess("");
  };

  const handleSelectPlace = async (placeId, description) => {
    try {
      setLoadingPlaces(true);
      setPlaceSuggestions([]);
      const { data } = await coreApi.get("/v1/places/details", { params: { placeId } });
      if (!data?.ok) {
        setFormError("Could not fetch address details. Try again.");
        return;
      }
      const { addressComponents, location } = data;
      const parsed = buildAddressFromComponents(addressComponents || []);
      setFormData((prev) => ({
        ...prev,
        ...parsed,
        name: prev.name || description || "",
        lat: location?.lat ?? prev.lat,
        long: location?.lng ?? prev.long,
      }));
      setPlaceQuery(description || parsed.address || "");
      setFormError("");
    } catch (err) {
      console.error("place details error", err);
      setFormError("Unable to fill address from Places. Please try again.");
    } finally {
      setLoadingPlaces(false);
    }
  };

  const handlePlaceAutocomplete = async (val) => {
    setPlaceQuery(val);
    if (!val || val.trim().length < 3) {
      setPlaceSuggestions([]);
      return;
    }
    try {
      setLoadingPlaces(true);
      const { data } = await coreApi.get("/v1/places/autocomplete", { params: { input: val.trim() } });
      if (data?.ok && Array.isArray(data.suggestions)) {
        setPlaceSuggestions(data.suggestions);
      } else {
        setPlaceSuggestions([]);
      }
    } catch (err) {
      console.error("places autocomplete error", err);
      setPlaceSuggestions([]);
    } finally {
      setLoadingPlaces(false);
    }
  };

  const handleClaimSelection = async () => {
    if (!selectedMasjid) return;
    setClaimError("");
    setClaimSuccess("");
    try {
      setClaiming(true);
      const { data } = await coreApi.post(
        `/v1/masjid/${encodeURIComponent(selectedMasjid.id)}/claim-admin-owner`
      );
      const message =
        data?.message ||
        `You're now the Admin Owner for ${selectedMasjid.name || "this masjid"}.`;
      setClaimSuccess(message);

      try {
        const rawProfile = localStorage.getItem("muslim_profile");
        const parsedProfile = rawProfile ? JSON.parse(rawProfile) : {};
        const updatedProfile = {
          ...parsedProfile,
          isAdmin: true,
          role: data?.role || "Admin Owner",
          adminRole: data?.role || "Admin Owner",
          masjidId: selectedMasjid.id,
          activeMasjidId: selectedMasjid.id,
        };
        localStorage.setItem("muslim_profile", JSON.stringify(updatedProfile));
      } catch (storageErr) {
        console.warn("Unable to update cached profile after claim:", storageErr);
      }

      setTimeout(() => {
        if (fromMobile) {
          window.location.href = "muslim://signup-complete";
        } else {
          window.location.replace("/masjid-dashboard");
        }
      }, 400);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err.message ||
        "Unable to continue. Please try again.";
      setClaimError(msg);
    } finally {
      setClaiming(false);
    }
  };

  const handleCreate = async () => {
    setFormError("");
    setSuccessMessage("");
    const required = ["name", "address", "city", "state", "zip"];
    for (const key of required) {
      if (!String(formData[key] ?? "").trim()) {
        setFormError(`Please enter ${key}.`);
        return;
      }
    }
    if (!formData.lat || !formData.long) {
      setFormError("Please use the Location field to select an address from Google Places.");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        lat: Number(formData.lat),
        long: Number(formData.long),
      };
      const { data } = await masjidApi.post("/v1/masjid", payload);
      if (!data?.ok) {
        setFormError(data?.error || "Failed to create masjid.");
        return;
      }
      const newMasjidId = data?.masjid?.id;

      try {
        const rawProfile = localStorage.getItem("muslim_profile");
        const parsedProfile = rawProfile ? JSON.parse(rawProfile) : {};
        const updatedProfile = {
          ...parsedProfile,
          isAdmin: true,
          role: "masjid",
          adminRole: "Admin Owner",
          masjidId: newMasjidId,
          activeMasjidId: newMasjidId,
          signupComplete: true,
          status: "active",
        };
        localStorage.setItem("muslim_profile", JSON.stringify(updatedProfile));
      } catch (storageErr) {
        console.warn("Unable to update cached profile after create:", storageErr);
      }

      setSuccessMessage("Masjid created! Redirecting...");
      setTimeout(() => {
        if (fromMobile) {
          window.location.href = "muslim://signup-complete";
        } else {
          window.location.replace("/masjid-dashboard");
        }
      }, 500);
    } catch (err) {
      console.error("create masjid error", err);
      const msg = err?.response?.data?.error || err.message || "Failed to create masjid.";
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EditorialShell>
      <SiteNav />
      <PatternTile opacity={0.04} />

      <main className="relative mx-auto px-4 py-14 sm:px-6 sm:py-20 lg:px-10" style={{ maxWidth: 1100 }}>
        <div className="space-y-10">
          {/* Header */}
          <div className="space-y-4 text-center">
            <div className="inline-flex">
              <Eyebrow number="01">Masjid admin setup</Eyebrow>
            </div>
            <SerifHeading
              level={1}
              style={{
                fontSize: "clamp(2.25rem, 5.5vw, 4.25rem)",
                lineHeight: 0.98,
                letterSpacing: "-0.03em",
              }}
            >
              Find or <em style={{ color: BRAND.gold, fontWeight: 400 }}>add</em>
              <br />
              your masjid.
            </SerifHeading>
            <p className="mx-auto max-w-2xl text-[17px]" style={{ color: palette.ink2, lineHeight: 1.55 }}>
              Search for your masjid. If it's not listed, add it with an accurate address so the
              community can find you.
            </p>
          </div>

          {/* Search card */}
          <EditorialCard variant="ivory" className="p-6 sm:p-8">
            <div className="mb-6 flex items-center gap-3">
              <Search className="h-5 w-5" style={{ color: BRAND.gold }} />
              <SerifHeading level={2} style={{ fontSize: 24, fontWeight: 500 }}>
                Search masajid
              </SerifHeading>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or location"
                className={inputClass}
                style={inputStyle}
              />
              <PillButton
                variant="outline"
                onClick={() => loadMasjids(searchTerm.trim())}
                className="justify-center sm:w-auto"
              >
                {loadingList ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
              </PillButton>
            </div>

            {listError && (
              <Alert
                className="mt-4"
                style={{
                  background: BRAND.terracotta + "15",
                  borderColor: BRAND.terracotta + "40",
                  color: BRAND.terracotta,
                }}
              >
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{listError}</AlertDescription>
              </Alert>
            )}

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {loadingList && (
                <div className="col-span-full flex justify-center py-6" style={{ color: palette.muted }}>
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              )}
              {!loadingList &&
                masjidResults.slice(0, 10).map((m) => {
                  const selected = selectedMasjidId === m.id;
                  return (
                    <button
                      key={m.id || m.name}
                      type="button"
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected}
                      onClick={() => handleSelectMasjid(m.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelectMasjid(m.id);
                        }
                      }}
                      className="relative rounded-2xl p-4 text-left transition-all"
                      style={{
                        background: selected ? palette.paper : "#ffffff",
                        border: `1px solid ${selected ? BRAND.gold : palette.hairline}`,
                        boxShadow: selected ? `0 4px 24px -6px rgba(191,154,47,0.2)` : "none",
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 space-y-1">
                          <p
                            className="truncate"
                            style={{
                              fontFamily: EDITORIAL_FONT.serif,
                              fontSize: 18,
                              fontWeight: 500,
                              color: palette.ink,
                            }}
                          >
                            {m.name}
                          </p>
                          <p className="text-sm" style={{ color: palette.muted }}>
                            {[m.address, m.city, m.state, m.zip].filter(Boolean).join(", ")}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
            </div>

            {selectedMasjid && (
              <div
                className="mt-5 rounded-xl px-4 py-3 text-sm"
                style={{
                  background: BRAND.gold + "10",
                  border: `1px solid ${BRAND.gold}40`,
                }}
              >
                <p
                  className="font-semibold"
                  style={{ fontFamily: EDITORIAL_FONT.serif, fontSize: 18, color: BRAND.goldDeep }}
                >
                  {selectedMasjid.name}
                </p>
                <p style={{ color: palette.muted }}>
                  {[selectedMasjid.address, selectedMasjid.city, selectedMasjid.state, selectedMasjid.zip]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm" style={{ color: palette.muted }}>
                Select your masjid to become the Admin Owner and continue.
              </div>
              <PillButton
                variant="primary"
                onClick={handleClaimSelection}
                disabled={!selectedMasjid || claiming}
                size="lg"
              >
                {claiming ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Continuing…
                  </>
                ) : (
                  <>
                    Continue
                    <span aria-hidden>→</span>
                  </>
                )}
              </PillButton>
            </div>

            {claimError && (
              <Alert
                className="mt-4"
                style={{
                  background: BRAND.terracotta + "15",
                  borderColor: BRAND.terracotta + "40",
                  color: BRAND.terracotta,
                }}
              >
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{claimError}</AlertDescription>
              </Alert>
            )}
            {claimSuccess && (
              <Alert
                className="mt-4"
                style={{
                  background: BRAND.sage + "15",
                  borderColor: BRAND.sage + "40",
                  color: BRAND.sage,
                }}
              >
                <AlertDescription>{claimSuccess}</AlertDescription>
              </Alert>
            )}

            {showAddMasjidCta && (
              <div
                className="mt-5 rounded-xl p-4 text-sm"
                style={{
                  background: palette.paper,
                  border: `1px solid ${palette.hairline}`,
                  color: palette.muted,
                }}
              >
                Can't find your masjid? Add it below to get started.
              </div>
            )}
          </EditorialCard>

          {/* Create card */}
          <EditorialCard variant="paper" className="p-6 sm:p-8">
            <div className="mb-6 flex items-center gap-3">
              <Plus className="h-5 w-5" style={{ color: BRAND.gold }} />
              <SerifHeading level={2} style={{ fontSize: 24, fontWeight: 500 }}>
                Add masjid
              </SerifHeading>
            </div>
            <p className="mb-6 text-sm" style={{ color: palette.muted }}>
              Required: name, address, city, state, zip. Use the location field to autofill via
              Google Places.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <label style={labelStyle}>Masjid name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Name"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <label style={labelStyle}>Location</label>
                <div className="relative">
                  <MapPin
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    style={{ color: palette.muted }}
                  />
                  <Input
                    value={placeQuery}
                    onChange={(e) => handlePlaceAutocomplete(e.target.value)}
                    placeholder="Start typing address…"
                    className={`${inputClass} pl-10`}
                    style={inputStyle}
                  />
                  {placeSuggestions.length > 0 && (
                    <div
                      className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl shadow-lg"
                      style={{
                        background: "#ffffff",
                        border: `1px solid ${palette.hairline}`,
                      }}
                    >
                      {placeSuggestions.map((s, idx) => (
                        <button
                          key={s.placeId}
                          type="button"
                          className="flex w-full items-start gap-2 px-4 py-3 text-left text-sm transition-colors"
                          style={{
                            borderBottom:
                              idx < placeSuggestions.length - 1
                                ? `1px solid ${palette.hairline}`
                                : "none",
                            color: palette.ink,
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = palette.paper)}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          onClick={() => handleSelectPlace(s.placeId, s.label || s.description)}
                        >
                          <MapPin className="mt-0.5 h-4 w-4" style={{ color: BRAND.gold }} />
                          <span>{s.label || s.description || "Address"}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {[
                ["address", "Address", "Street address"],
                ["city", "City", "City"],
                ["state", "State", "State"],
                ["zip", "Zip", "Zip code"],
              ].map(([key, label, placeholder]) => (
                <div key={key} className="space-y-2">
                  <label style={labelStyle}>{label}</label>
                  <Input
                    value={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    placeholder={placeholder}
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>
              ))}

              <div className="space-y-2 sm:col-span-2">
                <label className="flex items-center gap-2" style={labelStyle}>
                  Website <Globe2 className="h-3.5 w-3.5" style={{ color: palette.muted }} />
                </label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>
            </div>

            {formError && (
              <Alert
                className="mt-5"
                style={{
                  background: BRAND.terracotta + "15",
                  borderColor: BRAND.terracotta + "40",
                  color: BRAND.terracotta,
                }}
              >
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            {successMessage && (
              <Alert
                className="mt-5"
                style={{
                  background: BRAND.sage + "15",
                  borderColor: BRAND.sage + "40",
                  color: BRAND.sage,
                }}
              >
                <AlertDescription>{successMessage}</AlertDescription>
              </Alert>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm" style={{ color: palette.muted }}>
                You will become the Admin Owner of the masjid you create.
              </div>
              <PillButton
                variant="primary"
                onClick={handleCreate}
                disabled={submitting}
                size="lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  <>
                    Create & continue
                    <span aria-hidden>→</span>
                  </>
                )}
              </PillButton>
            </div>
          </EditorialCard>

          <div
            className="text-center text-sm"
            style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
          >
            Need help? Contact{" "}
            <a
              href="mailto:support@muslimplus.org"
              style={{ color: BRAND.gold, fontWeight: 600 }}
            >
              support@muslimplus.org
            </a>
          </div>
        </div>
      </main>
    </EditorialShell>
  );
}
